import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, TextField, Button, Typography, Alert,
  CircularProgress, InputAdornment, IconButton,
} from '@mui/material';
import LockOutlinedIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import api from '../services/api';
import FundoTecnologico from '../components/FundoTecnologico';
import CartaoAutenticacao from '../components/CartaoAutenticacao';
import { VERSION } from '../version';

const estiloBotaoPrincipal = {
  position: 'relative', overflow: 'hidden',
  background: 'linear-gradient(135deg, #13375F 0%, #e94560 130%)',
  py: 1.4, fontWeight: 700, fontSize: 15,
  boxShadow: '0 8px 24px rgba(233,69,96,0.35)',
  '&::after': {
    content: '""', position: 'absolute', top: 0, left: '-60%',
    width: '40%', height: '100%',
    background: 'linear-gradient(120deg, transparent, rgba(255,255,255,0.35), transparent)',
    transform: 'skewX(-20deg)',
    animation: 'shimmer 3.2s ease-in-out infinite',
  },
};

export default function RedefinirSenha() {
  const [searchParams] = useSearchParams();
  const uid = searchParams.get('uid');
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [erro, setErro] = useState('');
  const [sucesso, setSucesso] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const linkValido = Boolean(uid && token);

  const handleRedefinir = async () => {
    setErro('');
    if (!novaSenha || novaSenha.length < 8) {
      setErro('A senha precisa ter pelo menos 8 caracteres.');
      return;
    }
    if (novaSenha !== confirmarSenha) {
      setErro('As senhas não coincidem.');
      return;
    }
    setSalvando(true);
    try {
      await api.post('/auth/redefinir-senha/', { uid, token, nova_senha: novaSenha });
      setSucesso(true);
    } catch (err) {
      setErro(err?.response?.data?.error || 'Não foi possível redefinir a senha. O link pode ter expirado.');
    } finally {
      setSalvando(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: { xs: 'center', md: 'flex-end' } }}>
      <style>{`
        @keyframes derivaA { 0%,100% { transform: translate(0,0); } 50% { transform: translate(40px,30px); } }
        @keyframes derivaB { 0%,100% { transform: translate(0,0); } 50% { transform: translate(-35px,-25px); } }
        @keyframes pisoMove { from { background-position: 0 0, 0 0; } to { background-position: 0 54px, 0 0; } }
        @keyframes shimmer {
          0% { left: -60%; }
          100% { left: 130%; }
        }
        @media (prefers-reduced-motion: reduce) {
          [class*="deriva"], [class*="piso"] { animation: none !important; }
        }
      `}</style>

      <FundoTecnologico />

      <Box sx={{ position: 'absolute', left: { xs: '50%', md: 88 }, top: { xs: 56, md: '50%' }, transform: { xs: 'translateX(-50%)', md: 'translateY(-50%)' }, textAlign: { xs: 'center', md: 'left' }, zIndex: 1 }}>
        <Typography sx={{
          color: 'white', fontWeight: 800, letterSpacing: '0.01em', lineHeight: 0.95,
          fontSize: { xs: 42, md: 76 },
          background: 'linear-gradient(120deg, #ffffff 30%, #cfd8e3 60%, #ffffff 90%)',
          WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
        }}>
          LEAN<br />WAY
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.4)', fontWeight: 600, fontSize: { xs: 12, md: 15 }, letterSpacing: '0.35em', mt: 1 }}>
          CONSULTING
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.28)', fontSize: 11.5, fontFamily: 'monospace', mt: { xs: 3, md: 6 } }}>
          Sistema de Gestão · v{VERSION}
        </Typography>
      </Box>

      <CartaoAutenticacao>
        {!linkValido ? (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 1.5 }}>
              Link inválido
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Este link de redefinição de senha está incompleto ou incorreto. Solicite um novo na tela de login.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={{ backgroundColor: '#13375F', py: 1.2 }}>
              Voltar ao login
            </Button>
          </>
        ) : sucesso ? (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 1.5 }}>
              Senha redefinida!
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sua senha foi atualizada com sucesso. Já pode fazer login com a nova senha.
            </Typography>
            <Button fullWidth variant="contained" onClick={() => navigate('/login')} sx={{ backgroundColor: '#13375F', py: 1.2 }}>
              Ir para o login
            </Button>
          </>
        ) : (
          <>
            <Typography variant="h5" fontWeight={700} sx={{ color: '#0d1b2e', mb: 0.5 }}>
              Criar nova senha
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3.5 }}>
              Escolha uma nova senha para sua conta.
            </Typography>

            {erro && <Alert severity="error" sx={{ mb: 2.5 }}>{erro}</Alert>}

            <TextField
              fullWidth
              label="Nova senha"
              type={mostrarSenha ? 'text' : 'password'}
              value={novaSenha}
              onChange={(e) => setNovaSenha(e.target.value)}
              autoFocus
              sx={{ mb: 2.5 }}
              helperText="Mínimo de 8 caracteres"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setMostrarSenha(!mostrarSenha)} edge="end" size="small">
                        {mostrarSenha ? <VisibilityOffIcon sx={{ fontSize: 19 }} /> : <VisibilityIcon sx={{ fontSize: 19 }} />}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
              }}
            />

            <TextField
              fullWidth
              label="Confirmar nova senha"
              type={mostrarSenha ? 'text' : 'password'}
              value={confirmarSenha}
              onChange={(e) => setConfirmarSenha(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleRedefinir()}
              sx={{ mb: 3.5 }}
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={{ color: '#94a3b8', fontSize: 20 }} />
                    </InputAdornment>
                  ),
                },
              }}
            />

            <Button
              fullWidth
              variant="contained"
              size="large"
              onClick={handleRedefinir}
              disabled={salvando}
              sx={estiloBotaoPrincipal}
            >
              {salvando ? <CircularProgress size={24} sx={{ color: 'white' }} /> : 'Redefinir senha'}
            </Button>
          </>
        )}
      </CartaoAutenticacao>
    </Box>
  );
}